import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { DemoButton } from "@/components/public/DemoDialog";
import { Check, ArrowRight, Zap, Crown, Rocket, Building2, Clock } from "lucide-react";

// Four paid tiers + a 14-day Trial card above them. Pricing is tuned to the
// real COGS per interview: Starter ~55% margin, Growth ~36%, Scale upsold via
// overage, Enterprise negotiated. Overage captures runaway hiring months
// without blowing up the base price.
const plans = [
  {
    name: "Starter",
    icon: Zap,
    monthly: 149,
    annual: 119, // ~20% off
    tagline: "For founder-led teams running their first programmatic hiring.",
    cta: "Start free trial",
    ctaStyle: "outline",
    includes: [
      "30 candidate interviews / month",
      "3 recruiter seats · 1 workspace",
      "AI phone, video & coding rounds",
      "Branded PDF scorecards",
      "Email delivery (shared sender)",
      "Standard storage & analytics",
      "Email support",
    ],
    overage: "$6 per extra interview",
  },
  {
    name: "Growth",
    icon: Crown,
    monthly: 699,
    annual: 559,
    tagline: "For scaling TA teams that want their org structure built in.",
    cta: "Start free trial",
    ctaStyle: "solid",
    popular: true,
    includes: [
      "200 candidate interviews / month",
      "10 seats · unlimited workspaces",
      "Everything in Starter, plus:",
      "TAG Head → TA Lead → Recruiter hierarchy",
      "Org chart & reporting lines",
      "Bring-your-own Twilio & B2 (cost pass-through)",
      "Custom-domain sender for emails",
      "Priority email + chat support",
    ],
    overage: "$4 per extra interview",
  },
  {
    name: "Scale",
    icon: Rocket,
    monthly: 2499,
    annual: 1999,
    tagline: "For enterprise hiring programs with compliance and API needs.",
    cta: "Start free trial",
    ctaStyle: "outline",
    includes: [
      "1,000 candidate interviews / month",
      "Unlimited seats · unlimited workspaces",
      "Everything in Growth, plus:",
      "SSO / SAML + SCIM provisioning",
      "SOC-2 Type II report access",
      "API access & webhooks",
      "Dedicated storage pool",
      "99.9% uptime SLA",
    ],
    overage: "$3 per extra interview",
  },
  {
    name: "Enterprise",
    icon: Building2,
    monthly: "Custom",
    annual: "Custom",
    tagline: "For global hiring programs doing >1,000 interviews / month.",
    cta: "Contact sales",
    ctaStyle: "outline",
    includes: [
      "Unlimited interviews (negotiated)",
      "Everything in Scale, plus:",
      "Dedicated success manager",
      "Custom retention + data residency (EU, India, US)",
      "Private cloud / BYO cloud option",
      "ATS integrations (Greenhouse, Lever, Workday)",
      "Custom legal / DPA / MSA",
      "24/7 priority support",
    ],
    overage: "Volume-negotiated",
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(true);
  return (
    <PublicLayout>
      <section className="relative -mt-16 pt-16 bg-gradient-to-b from-indigo-50/70 via-white to-white overflow-hidden" data-testid="pricing-hero">
        <div className="absolute inset-0 rs-grid-bg [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
        <div className="absolute left-1/2 -translate-x-1/2 -top-40 w-[700px] h-[420px] rounded-full bg-indigo-200/40 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-28 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold font-mono text-indigo-600 mb-5">
          <span className="w-6 h-px bg-indigo-600" /> Simple pricing · usage-based overage
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">Pricing that scales with your hiring</h1>
        <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
          Start with a 14-day trial. No credit card required. Upgrade or cancel anytime.
        </p>

        {/* Monthly / Annual toggle */}
        <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200" data-testid="billing-toggle">
          <button
            onClick={() => setAnnual(false)}
            data-testid="toggle-monthly"
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            data-testid="toggle-annual"
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
          >
            Annual <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Save 20%</span>
          </button>
        </div>
        </div>
      </section>

      {/* 14-day trial banner */}
      <section className="relative max-w-5xl mx-auto px-4 sm:px-6 -mt-14 pb-12">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="trial-banner">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 text-white grid place-items-center flex-shrink-0">
              <Clock className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-slate-900 text-lg">Try everything free for 14 days</div>
              <div className="text-sm text-slate-600">Full access, 10 candidate interviews included, no credit card required.</div>
            </div>
          </div>
          <Link to="/register" data-testid="trial-cta" className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors">
            Start trial <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Pricing grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((p) => {
          const Icon = p.icon;
          const price = annual ? p.annual : p.monthly;
          return (
            <div
              key={p.name}
              data-testid={`plan-${p.name.toLowerCase()}`}
              className={`relative rounded-2xl p-6 sm:p-7 flex flex-col ${p.popular ? "bg-gradient-to-b from-indigo-50 to-white border-2 border-indigo-500 shadow-2xl shadow-indigo-500/15 lg:scale-105 lg:z-10" : "bg-white border border-slate-200"}`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[11px] font-semibold px-3 py-1 rounded-full tracking-wide">MOST POPULAR</div>
              )}
              <div className="flex items-center gap-2 mb-4">
                <Icon className={`w-5 h-5 ${p.popular ? "text-indigo-600" : "text-indigo-500"}`} />
                <div className={`text-sm font-semibold ${p.popular ? "text-indigo-700" : "text-slate-500"}`}>{p.name}</div>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                {typeof price === "number" ? (
                  <>
                    <div className="text-3xl sm:text-4xl font-bold">${price}</div>
                    <div className={`text-sm ${"text-slate-500"}`}>/mo</div>
                  </>
                ) : (
                  <div className="text-3xl sm:text-4xl font-bold">Custom</div>
                )}
              </div>
              {typeof price === "number" && annual && (
                <div className={`text-xs ${"text-emerald-600"}`}>Billed annually · ${price * 12}/yr</div>
              )}
              <div className={`text-sm mt-3 min-h-[40px] ${"text-slate-600"}`}>{p.tagline}</div>
              {p.name === "Enterprise" ? (
                <DemoButton className="mt-5 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full font-semibold text-sm transition-colors border border-slate-300 hover:border-slate-900 text-slate-900" data-testid="cta-enterprise">
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </DemoButton>
              ) : (
              <Link
                to="/register"
                className={`mt-5 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full font-semibold text-sm transition-colors ${p.ctaStyle === "solid" ? "bg-indigo-500 hover:bg-indigo-400 text-white" : p.popular ? "border border-indigo-300 hover:border-indigo-600 text-indigo-700" : "border border-slate-300 hover:border-slate-900 text-slate-900"}`}
                data-testid={`cta-${p.name.toLowerCase()}`}
              >
                {p.cta} <ArrowRight className="w-4 h-4" />
              </Link>
              )}
              <ul className="mt-6 space-y-3 flex-1">
                {p.includes.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${"text-indigo-600"}`} />
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
              <div className={`mt-5 pt-4 border-t ${p.popular ? "border-indigo-100" : "border-slate-100"}`}>
                <div className={`text-[11px] uppercase tracking-wider ${"text-slate-500"}`}>Overage</div>
                <div className={`text-sm font-medium mt-0.5 ${"text-slate-900"}`}>{p.overage}</div>
              </div>
            </div>
          );
        })}
      </section>

      {/* FAQ-ish */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">What counts as an interview?</div>
            <div className="text-sm text-slate-600">A candidate who completes at least one stage — phone, video, or coding. Invites that never start are free.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">What happens if we go over the limit?</div>
            <div className="text-sm text-slate-600">We don't stop you. Overage is billed at the per-interview rate on your plan and shown on your next invoice.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Can we bring our own Twilio / storage?</div>
            <div className="text-sm text-slate-600">Yes — on Growth and above, your phone minutes and recording storage bill directly to your accounts, lowering your effective per-interview cost.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Is there a free plan?</div>
            <div className="text-sm text-slate-600">We offer a 14-day free trial with 10 interviews. We don't offer a perpetual free plan — it's unsustainable at our per-interview cost.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Can we change plans anytime?</div>
            <div className="text-sm text-slate-600">Yes. Upgrades are prorated. Downgrades take effect next billing cycle. Cancel anytime from Settings.</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900 mb-1">Do you offer discounts for startups?</div>
            <div className="text-sm text-slate-600">Yes — 50% off the first year of Starter or Growth for YC, Techstars, and other accelerator alumni. <a href="mailto:hello@recruitsift.com" className="text-blue-600 hover:underline">Email us</a>.</div>
          </div>
        </div>
      </section>

      {/* Enterprise contact */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-slate-200 p-6 sm:p-8 bg-slate-50/60 text-center">
          <h3 className="text-xl font-bold text-slate-900">Need something custom?</h3>
          <p className="text-slate-600 mt-2">Doing &gt;1,000 interviews a month? Multi-region deployment? Public-sector compliance? Let's put together a plan.</p>
          <DemoButton data-testid="pricing-talk-to-sales" className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors">
            Talk to sales <ArrowRight className="w-4 h-4" />
          </DemoButton>
        </div>
      </section>
    </PublicLayout>
  );
}
